import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RotateCcw } from "lucide-react";
import {
  usePlayoutCounters,
  useResetPlayoutCounters,
  useRunLog,
} from "@/hooks/useBridgePlayout";

interface PlayoutLogProps {
  bridgeUrl: string | undefined | null;
}

export function PlayoutLog({ bridgeUrl }: PlayoutLogProps) {
  const { data: counters } = usePlayoutCounters(bridgeUrl);
  const { data: runs = [] } = useRunLog(bridgeUrl);
  const reset = useResetPlayoutCounters(bridgeUrl);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>Counters</CardTitle>
            <CardDescription>
              {counters?.since
                ? `Since ${new Date(counters.since).toLocaleString()}`
                : "Since the beginning"}
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => reset.mutate()}
            data-testid="reset-counters"
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            Reset
          </Button>
        </CardHeader>
        <CardContent>
          {counters?.counters.length ? (
            <div className="space-y-1">
              {counters.counters.map((counter) => (
                <div
                  key={counter.playout_id}
                  className="flex items-center gap-2 rounded border p-2 text-sm"
                >
                  <span className="flex-1 truncate">
                    {counter.playout_name}
                  </span>
                  <Badge variant="secondary">{counter.played} played</Badge>
                  {counter.skipped > 0 && (
                    <Badge variant="outline">{counter.skipped} skipped</Badge>
                  )}
                  {counter.failed > 0 && (
                    <Badge variant="destructive">{counter.failed} failed</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nothing played yet.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>As-run log</CardTitle>
          <CardDescription>
            Times are stored in UTC and shown in your local timezone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No runs yet.</p>
          ) : (
            <div className="space-y-3">
              {runs.map((run) => (
                <div key={run.id} className="rounded-md border p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{run.playlist_name}</span>
                    <Badge variant="outline">{run.started_by}</Badge>
                    <Badge
                      variant={
                        run.status === "completed" ? "secondary" : "outline"
                      }
                    >
                      {run.status}
                    </Badge>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {new Date(run.anchored_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {run.items.map((item) => (
                      <div
                        key={item.id}
                        className="flex gap-2 text-xs text-muted-foreground"
                      >
                        <span className="w-16 shrink-0">{item.result}</span>
                        {/* A looping run logs the same items once per pass,
                            so the rows only tell each other apart by loop. */}
                        {item.cycle > 0 && (
                          <span className="w-14 shrink-0">
                            loop {item.cycle + 1}
                          </span>
                        )}
                        <span className="flex-1">
                          {item.on_air_at
                            ? `on air ${new Date(item.on_air_at).toLocaleTimeString()}`
                            : (item.skip_reason?.replace(/_/g, " ") ??
                              item.error ??
                              "")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
